import {useEffect, useRef} from "react";
import {useLocation} from "react-router-dom";
import {makeStyles} from "@mui/styles";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav
} from "context";
import React, {useState} from "react";
import AppMenu from "./AppMenu";

function Sidenav({key, item, color, brand, brandName, collapse, link, ...rest}) {
  const classes = useStyles();
  const [controller, dispatch] = useMaterialUIController();
  const {miniSidenav, transparentSidenav, whiteSidenav, darkMode} = controller;
  const location = useLocation();

  // ✅ Correct initial value synchronously — no flash
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1200);

  // ✅ Track if this is the very first mount — skip auto-close on initial load
  const isFirstMount = useRef(true);

  // ✅ Track if user manually opened the sidebar
  const userOpenedSidenav = useRef(false);

  let textColor = "white";
  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => {
    userOpenedSidenav.current = false; // ✅ reset flag on close
    setMiniSidenav(dispatch, true);
  };

  // ✅ Listen for burger menu click event from DashboardNavbar
  useEffect(() => {
    const handleUserOpen = () => {
      userOpenedSidenav.current = true;
    };
    window.addEventListener("userOpenedSidenav", handleUserOpen);
    return () => window.removeEventListener("userOpenedSidenav", handleUserOpen);
  }, []);

  // ✅ Effect 1: runs only on mount + window resize (no location)
  useEffect(() => {
    function handleMiniSidenav() {
      const mobile = window.innerWidth < 1200;
      setIsMobile(mobile);

      // ✅ Don't force-close if user manually opened the sidebar
      if (!userOpenedSidenav.current) {
        setMiniSidenav(dispatch, mobile);
      }

      if (mobile) {
        setTransparentSidenav(dispatch, false);
        setWhiteSidenav(dispatch, true);
      } else {
        setTransparentSidenav(dispatch, transparentSidenav);
        setWhiteSidenav(dispatch, whiteSidenav);
      }
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav(); // run once on mount

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch]); // ✅ no location here

  // ✅ Effect 2: auto-close on route change — skip very first mount
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    if (isMobile) {
      userOpenedSidenav.current = false; // reset on navigate
      setMiniSidenav(dispatch, true);
    }
  }, [location.pathname]);

  // ✅ Effect 3: body scroll lock when sidebar open on mobile
  useEffect(() => {
    if (!miniSidenav && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [miniSidenav, isMobile]);

  return (
    <SidenavRoot
      {...rest}
      variant={isMobile ? "temporary" : "permanent"}
      open={!miniSidenav}
      onClose={isMobile ? closeSidenav : undefined}
      ownerState={{transparentSidenav, whiteSidenav, miniSidenav, darkMode}}
    >
      <MDBox px={4} textAlign="center">
        <MDBox
          display={{xs: "block", xl: "none"}}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{cursor: "pointer"}}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{fontWeight: "bold"}}>close</Icon>
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox
        sx={{
          width: "100%",
          maxWidth: 360,
          pt: {xs: 3, xl: 0},
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(24, 54, 122, 0.35) transparent",
          "&::-webkit-scrollbar": {
            width: "6px"
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent"
          },
          "&::-webkit-scrollbar-thumb": {
            background: "linear-gradient(180deg, #53B0E0, #18367A)",
            borderRadius: "10px"
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#18367A"
          },
          "&::-webkit-scrollbar-button": {
            display: "none",
            height: 0,
            width: 0
          }
        }}
      >
        <AppMenu classes={{paper: classes.drawerPaper}} />
      </MDBox>
    </SidenavRoot>
  );
}

const drawerWidth = 240;

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex"
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    background: "#fff",
    color: "#535454"
  },
  content: {
    flexGrow: 1,
    height: "100vh",
    overflow: "auto"
  },
  container: {}
}));

Sidenav.defaultProps = {
  color: "dark",
  brand: ""
};

Sidenav.PropTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "bntColor",
    "success",
    "warning",
    "error",
    "dark"
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired
};

export default Sidenav;